import React, { useState, useEffect, Dispatch, SetStateAction } from "react";
import { NumberSize, ResizeDirection } from "re-resizable";
import { getNewWindowLocation, getNewWindowSize } from "./preview.biz";

type RelState = {
  x: number;
  y: number;
};

export interface PreviewState {
  x: number;
  y: number;
  rel: RelState | null;
  dragging: boolean;
}

function usePreviewSelector(
  setOpen: Dispatch<SetStateAction<boolean>>,
  setShowPortal: Dispatch<SetStateAction<boolean>>
) {
  const [size, setSize] = useState({ width: 500, height: 500 });

  const [preview, setPreview] = useState<PreviewState>({
    x: 100,
    y: 100,
    rel: null,
    dragging: false,
  });

  const enableDragging = (e: React.MouseEvent<any>) => {
    if (e.button !== 0) return;

    setPreview((prev) => ({
      ...prev,
      dragging: true,
      rel: {
        x: e.pageX - preview.x,
        y: e.pageY - preview.y,
      },
    }));

    e.stopPropagation();
    e.preventDefault();
  };

  const disableDragging = (e: React.MouseEvent<any>) => {
    setPreview((prev) => ({ ...prev, dragging: false }));
    e.stopPropagation();
    e.preventDefault();
  };

  const onMouseMove = (e: any) => {
    if (!preview.dragging) return;
    const dialogWidth = document.getElementById("node")?.clientWidth || 0;
    const dialogHeight = document.getElementById("node")?.clientHeight || 0;
    setPreview(getNewWindowLocation(e, dialogWidth, dialogHeight, preview));
    e.stopPropagation();
    e.preventDefault();
  };

  useEffect(() => {
    if (preview.dragging) {
      document.addEventListener("mousemove", onMouseMove);
    } else {
      document.removeEventListener("mousemove", onMouseMove);
    }

    return () => document.removeEventListener("mousemove", onMouseMove);
  }, [preview.dragging]);

  const handlePortalOpen = () => {
    setShowPortal(true);
    handleClose();
  };

  const handlePortalClose = () => {
    setShowPortal(false);
    setSize({ width: 500, height: 500 });
  };

  const handleClose = () => {
    setOpen(false);
    setSize({ width: 500, height: 500 });
  };

  const resizeStop = (
    e: any,
    direction: ResizeDirection,
    ref: HTMLElement,
    d: NumberSize
  ) => {
    setSize(getNewWindowSize(size, d));
  };

  return {
    resizeStop,
    handleClose,
    handlePortalClose,
    handlePortalOpen,
    onMouseMove,
    disableDragging,
    enableDragging,
    preview,
    size,
    setSize
  };
}

export default usePreviewSelector;
